import React, { useState, useEffect } from 'react';
import { authService } from '../services/api';

function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const user = authService.getCurrentUser();

  useEffect(() => {
    loadMyOrders();
  }, []);

  const loadMyOrders = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://b1.envicblue.co.uk/api/orders.php?action=my-orders', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        // Ensure data.data is an array
        const ordersData = Array.isArray(data.data) ? data.data : [];
        setOrders(ordersData);
      } else {
        setOrders([]);
        setError(data.message || 'Failed to load orders');
      }
    } catch (err) {
      setOrders([]);
      setError('Failed to load orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'approved': return '#10b981';
      case 'rejected': return '#ef4444';
      case 'completed': return '#667eea';
      default: return '#666';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'approved': return '✅';
      case 'rejected': return '❌';
      case 'completed': return '📦';
      default: return '📋';
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filterStatus === 'all') return true;
    return order.status === filterStatus;
  });

  const orderStats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    approved: orders.filter(o => o.status === 'approved').length,
    rejected: orders.filter(o => o.status === 'rejected').length
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px' }}>⏳</div>
          <div style={{ color: '#666', marginTop: '10px' }}>Loading your orders...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
          🛒 My Orders
        </h1>
        <p style={{ color: '#666' }}>View and track your order requests</p>
      </div>

      {error && (
        <div style={{
          background: '#fee',
          border: '1px solid #fcc',
          color: '#c33',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Statistics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '16px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '10px' }}>📋</div>
          <div style={{ color: '#666', fontSize: '14px', marginBottom: '5px' }}>Total Orders</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#667eea' }}>{orderStats.total}</div>
        </div>

        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '16px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '10px' }}>⏳</div>
          <div style={{ color: '#666', fontSize: '14px', marginBottom: '5px' }}>Pending</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f59e0b' }}>{orderStats.pending}</div>
        </div>

        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '16px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '10px' }}>✅</div>
          <div style={{ color: '#666', fontSize: '14px', marginBottom: '5px' }}>Approved</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981' }}>{orderStats.approved}</div>
        </div>

        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '16px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '10px' }}>❌</div>
          <div style={{ color: '#666', fontSize: '14px', marginBottom: '5px' }}>Rejected</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#ef4444' }}>{orderStats.rejected}</div>
        </div>
      </div>

      {/* Filter */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '16px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        marginBottom: '20px'
      }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#333' }}>
          📊 Filter by Status
        </label>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '10px',
            border: '2px solid #e0e0e0',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none',
            cursor: 'pointer',
            minWidth: '200px'
          }}
        >
          <option value="all">All Orders</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '60px 20px',
          textAlign: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>📦</div>
          <h3 style={{ fontSize: '20px', color: '#333', marginBottom: '10px' }}>No Orders Found</h3>
          <p style={{ color: '#666' }}>You haven't placed any orders yet</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {filteredOrders.map((order) => (
            <div key={order.order_id} style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.08)';
            }}>
              {/* Order Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
                marginBottom: '15px',
                paddingBottom: '15px',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '5px' }}>
                    Order #{order.order_id}
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                    {order.product_name}
                  </h3>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {order.product_code}
                  </div>
                </div>
                <span style={{
                  padding: '8px 16px',
                  background: `${getStatusColor(order.status)}20`,
                  color: getStatusColor(order.status),
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  {getStatusIcon(order.status)} {order.status?.toUpperCase()}
                </span>
              </div>

              {/* Order Details */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '15px'
              }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '5px' }}>Quantity</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#667eea' }}>
                    {order.quantity} units
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '5px' }}>Order Date</div>
                  <div style={{ fontSize: '14px', color: '#333' }}>
                    📅 {new Date(order.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>

                {order.approved_by && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#999', marginBottom: '5px' }}>Approved By</div>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      👤 {order.approved_by}
                    </div>
                  </div>
                )}

                {order.notes && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: '12px', color: '#999', marginBottom: '5px' }}>Notes</div>
                    <div style={{
                      fontSize: '14px',
                      color: '#666',
                      padding: '10px',
                      background: '#f9f9f9',
                      borderRadius: '8px',
                      fontStyle: 'italic'
                    }}>
                      💬 {order.notes}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyOrders;
